"use client";

import React, { useState, useEffect } from "react";
import { PrivacySection } from "@/data/privacyPolicyData";

interface PrivacyContentSectionProps {
  sections: PrivacySection[];
}

export default function PrivacyContentSection({
  sections,
}: PrivacyContentSectionProps) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id || "");

  // Handle scroll spy
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200; // Offset for sticky header/hero

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveId(section.id);
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Adjust based on sticky header height
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setActiveId(id);
    }
  };

  return (
    <section className="bg-brand-black px-5 py-10 md:px-12 md:py-20 lg:px-[100px]">
      <div className="flex flex-col-reverse gap-10 lg:flex-row lg:gap-20">
        {/* Left Side (formerly Right) - Content Display */}
        <div className="w-full lg:w-2/3">
          <div className="flex flex-col space-y-10">
            {sections.map((section) => (
              <div key={section.id} id={section.id} className="scroll-mt-32">
                <h2 className="mb-2.5 text-xl font-bold text-white">
                  {section.title}
                </h2>
                <div className="prose prose-invert max-w-none space-y-4 text-base leading-[1.3] text-white/80">
                  {section.content}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side (formerly Left) - Navigation Box */}
        <div className="w-full lg:w-1/3">
          <div className="from-brand-red to-brand-dark-red sticky top-32 rounded-2xl bg-linear-to-br px-8 py-2">
            <div className="flex flex-col">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`cursor-pointer border-b border-white py-2.5 text-left text-sm text-white transition-all duration-300 last:border-0 hover:opacity-90 ${
                    activeId === section.id ? "font-bold" : "font-normal"
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
